/**
 * Administration > Terms of Service
 * Per-tenant markdown text + auto-bump version on save (forces re-acceptance).
 */
import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { listClients, updateClientSettings } from "@/lib/api";
import { FileText } from "lucide-react";

export default function AdminTermsOfService() {
  const { hasRole, assignments } = useAuth();
  const isSuper = hasRole(["super_admin"]);
  const myClientIds = useMemo(
    () => assignments.filter((a) => a.client_id).map((a) => a.client_id),
    [assignments]
  );

  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [markdown, setMarkdown] = useState("");
  const [originalMarkdown, setOriginalMarkdown] = useState("");
  const [version, setVersion] = useState(0);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await listClients();
      if (cancelled) return;
      const rows = (data || []).filter((c) => isSuper || myClientIds.includes(c.id));
      setClients(rows);
      setClientId((prev) => prev || rows[0]?.id || "");
    })();
    return () => { cancelled = true; };
  }, [isSuper, myClientIds]);

  useEffect(() => {
    if (!clientId) return undefined;
    let cancelled = false;
    (async () => {
      const { data } = await import("@/lib/supabaseClient").then(m => m.supabase
        .from("lbs_clients")
        .select("tos_markdown, tos_version")
        .eq("id", clientId)
        .maybeSingle());
      if (cancelled || !data) return;
      setMarkdown(data.tos_markdown || "");
      setOriginalMarkdown(data.tos_markdown || "");
      setVersion(data.tos_version || 0);
    })();
    return () => { cancelled = true; };
  }, [clientId]);

  const isDirty = markdown !== originalMarkdown;

  const save = async () => {
    if (!clientId) return;
    setSaving(true);
    setErr(null);
    const { error } = await updateClientSettings(clientId, {
      tos_markdown: markdown || null,
      tos_version: (version || 0) + (isDirty ? 1 : 0),
      tos_updated_at: isDirty ? new Date().toISOString() : undefined,
    });
    setSaving(false);
    if (error) {
      setErr(error.message || JSON.stringify(error));
      return;
    }
    toast.success(
      isDirty
        ? `ToS saved (v${(version || 0) + 1}) — users will be prompted to re-accept`
        : "Saved (no text changes)"
    );
    setOriginalMarkdown(markdown);
    setVersion((v) => v + (isDirty ? 1 : 0));
  };

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-4" data-testid="terms-of-service-page">
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <FileText className="h-5 w-5" /> Terms of Service
        </h1>
        <p className="text-sm text-slate-500">
          Tenant-specific ToS. Users must accept on first login and again after any change.
          Public booking links show the current text above the Submit button.
        </p>
      </div>

      {clients.length > 1 && (
        <div className="space-y-1">
          <Label className="text-xs">Tenant</Label>
          <Select value={clientId} onValueChange={setClientId}>
            <SelectTrigger className="w-72" data-testid="tos-client-picker">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {clients.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {err && <Alert variant="destructive"><AlertDescription>{err}</AlertDescription></Alert>}

      <Card>
        <CardHeader>
          <CardTitle className="text-sm flex items-center justify-between">
            <span>Markdown</span>
            <span className="text-xs font-normal text-slate-500">
              Current version: v{version}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Textarea
            rows={18}
            value={markdown}
            onChange={(e) => setMarkdown(e.target.value)}
            placeholder="Paste your Terms of Service here. Plain text or Markdown — headings, lists and links all render."
            className="font-mono text-sm"
            data-testid="tos-markdown-input"
          />
          <div className="flex items-center justify-end gap-2">
            <span className="text-xs text-slate-500 mr-auto">
              {isDirty ? "Unsaved changes — saving will bump version and force re-acceptance" : "Up to date"}
            </span>
            <Button
              onClick={save}
              disabled={saving || !clientId}
              data-testid="tos-save-btn"
            >
              {saving ? "Saving…" : isDirty ? `Save & bump to v${version + 1}` : "Save"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
